import React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useBooking } from "@/context/booking"
import { createBooking } from "@/lib/api"
import { getBookingPlatform } from "@/lib/cookies"

type BookingFormProps = {
  children?: React.ReactNode
  onSubmit?: (payload: {
    location: "tan_dinh" | "thu_duc"
    guests: number
    date: string
    time: string
    name?: string
    phone?: string
    seatingArea?: "phong_lanh" | "ngoai_troi"
  }) => void
}

const BookingForm: React.FC<BookingFormProps> = ({ children, onSubmit }) => {
  const { open, setOpen } = useBooking()
  const [location, setLocation] = React.useState<"tan_dinh" | "thu_duc">(
    "tan_dinh"
  )
  const [guests, setGuests] = React.useState(2)
  const [date, setDate] = React.useState("")
  const [time, setTime] = React.useState("")
  const [name, setName] = React.useState("")
  const [phone, setPhone] = React.useState("")
  const [seatingArea, setSeatingArea] = React.useState<
    "phong_lanh" | "ngoai_troi"
  >("phong_lanh")
  const [submitted, setSubmitted] = React.useState(false)
  const [dateError, setDateError] = React.useState("")

  // Danh sách ngày không nhận đặt bàn (format: YYYY-MM-DD)
  // Có thể block theo chi nhánh: { date, locations } — nếu không có locations thì block tất cả
  const BLOCKED_DATES_CONFIG: { date: string; locations?: ("tan_dinh" | "thu_duc")[] }[] = [
    { date: "2026-02-17", locations: ["thu_duc"] }, // Mùng 1: chỉ block Thủ Đức, Tân Định vẫn nhận
  ]
  const BLOCKED_DATES = BLOCKED_DATES_CONFIG
    .filter(b => !b.locations || b.locations.includes(location))
    .map(b => b.date)
  const TODAY_VN = React.useMemo(() => {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Ho_Chi_Minh",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    return formatter.format(new Date())
  }, [])

  const NOW_TIME_VN = React.useMemo(() => {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Ho_Chi_Minh",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    return formatter.format(new Date())
  }, [])

  const timeOptions = React.useMemo(() => {
    const [curH, curM] = NOW_TIME_VN.split(":").map(Number)
    const currentMinutes = curH * 60 + curM
    const openingMinutes = 10 * 60
    const closingMinutes = 21 * 60
    const startMinutes =
      date === TODAY_VN
        ? Math.ceil((currentMinutes + 1) / 30) * 30
        : openingMinutes
    const slots: string[] = []
    for (
      let m = Math.max(startMinutes, openingMinutes);
      m <= closingMinutes;
      m += 30
    ) {
      const hh = String(Math.floor(m / 60)).padStart(2, "0")
      const mm = String(m % 60).padStart(2, "0")
      slots.push(`${hh}:${mm}`)
    }
    return slots
  }, [date, NOW_TIME_VN, TODAY_VN])

  React.useEffect(() => {
    const formatVN = (d: Date) =>
      new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Ho_Chi_Minh",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(d)

    // Nếu chưa có date hoặc date hiện tại bị block (vd: đổi chi nhánh) → nhảy sang ngày kế
    if (!date || BLOCKED_DATES.includes(date)) {
      let d = date ? new Date(date + "T00:00:00") : new Date()
      let formatted = formatVN(d)
      while (BLOCKED_DATES.includes(formatted)) {
        d.setDate(d.getDate() + 1)
        formatted = formatVN(d)
      }
      setDate(formatted)
      setDateError("")
    }
  }, [TODAY_VN, date, location])

  React.useEffect(() => {
    if (timeOptions.length && (!time || !timeOptions.includes(time))) {
      setTime(timeOptions[0])
    }
  }, [timeOptions])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit?.({ location, guests, date, time, name, phone, seatingArea })

    // Get platform from cookie for tracking (fb from /order, tiktok from /reservation)
    const platform = getBookingPlatform()

    const payload = {
      dia_diem: location,
      so_luong_khach: guests,
      ngay: date.split("-").reverse().join("-"), // yyyy-mm-dd -> dd-mm-yyyy
      thoi_gian: time,
      ten: name,
      so_dien_thoai: phone,
      khu_vuc: seatingArea,
      platform, // Include platform source for tracking
    }
    try {
      await createBooking(payload)
      setSubmitted(true)
    } catch (err) {
      console.error("Booking failed", err)
    }
  }

  function handleClose() {
    setSubmitted(false)
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent>
        {submitted ? (
          <div className="text-center py-8">
            <DialogHeader>
              <DialogTitle className="text-xl text-center text-green-600">
                Đặt bàn thành công!
              </DialogTitle>
            </DialogHeader>
            <p className="mt-4 text-gray-700">
              Chúng tôi đã nhận được yêu cầu đặt bàn của bạn.
            </p>
            <p className="text-gray-700">
              Nhân viên sẽ liên hệ xác nhận trong thời gian sớm nhất.
            </p>
            <Button className="mt-6 text-white" onClick={handleClose}>
              Đóng
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Đặt bàn</DialogTitle>
              <DialogDescription>
                Nhập thông tin đặt bàn của bạn
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-2">
                <label htmlFor="location" className="text-sm font-medium">
                  Địa điểm
                </label>
                <select
                  id="location"
                  value={location}
                  onChange={e =>
                    setLocation(e.target.value as "tan_dinh" | "thu_duc")
                  }
                  className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                  required
                >
                  <option value="tan_dinh">Nhà hàng Tân Định</option>
                  <option value="thu_duc">Nhà hàng Thủ Đức</option>
                </select>
              </div>

              <div className="grid gap-2">
                <label htmlFor="seatingArea" className="text-sm font-medium">
                  Khu vực
                </label>
                <select
                  id="seatingArea"
                  value={seatingArea}
                  onChange={e =>
                    setSeatingArea(
                      e.target.value as "phong_lanh" | "ngoai_troi"
                    )
                  }
                  className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                  required
                >
                  <option value="phong_lanh">Phòng lạnh</option>
                  <option value="ngoai_troi">Ngoài trời</option>
                </select>
              </div>
              <div className="grid gap-2">
                <label htmlFor="guests" className="text-sm font-medium">
                  Số lượng khách
                </label>
                <input
                  id="guests"
                  type="number"
                  min={1}
                  value={guests}
                  onChange={e => setGuests(Number(e.target.value))}
                  className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <label htmlFor="date" className="text-sm font-medium">
                    Ngày
                  </label>
                  <input
                    id="date"
                    type="date"
                    value={date}
                    onChange={e => {
                      const val = e.target.value
                      if (BLOCKED_DATES.includes(val)) {
                        setDateError("Ngày này nhà hàng không nhận đặt bàn. Vui lòng chọn ngày khác.")
                        return
                      }
                      setDateError("")
                      setDate(val)
                    }}
                    min={TODAY_VN}
                    className={`h-10 w-full rounded-md border px-3 focus:outline-none focus:ring-2 focus:ring-primary ${dateError ? "border-red-500" : "border-gray-300"}`}
                    required
                  />
                  {dateError && (
                    <p className="text-xs text-red-500 mt-1">{dateError}</p>
                  )}
                </div>

                <div className="grid gap-2">
                  <label htmlFor="time" className="text-sm font-medium">
                    Thời gian
                  </label>
                  <select
                    id="time"
                    value={time}
                    onChange={e => setTime(e.target.value)}
                    className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                    required
                  >
                    {timeOptions.map(t => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                    {!timeOptions.length && (
                      <option value="" disabled>
                        Không còn khung giờ phù hợp hôm nay
                      </option>
                    )}
                  </select>
                </div>
              </div>

              <div className="grid gap-2">
                <label htmlFor="name" className="text-sm font-medium">
                  Tên
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Nhập tên của bạn"
                  className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div className="grid gap-2">
                <label htmlFor="phone" className="text-sm font-medium">
                  Số điện thoại
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="VD: 0912 345 678"
                  pattern="^[0-9\s+()-]{8,}$"
                  className="h-10 w-full rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Hủy
                </Button>
                <Button type="submit" className="text-white">
                  Đặt bàn
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default BookingForm
